import React, { useEffect, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { toast } from "sonner";
import { Shield, FileDown, Check } from "lucide-react";
import { PrimeVisionLogo } from "@/components/PrimeVisionLogo";

/**
 * Modale full-screen che blocca l'area clinica del medico finché non accetta
 * l'informativa di responsabilità. Versione tracciata server-side (`/medico/responsabilita`).
 */
export default function ResponsabilitaGate({ user, onAccepted }) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [checked, setChecked] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const { data } = await api.get("/medico/responsabilita");
        if (mounted) setData(data);
        if (!data.required) onAccepted && onAccepted();
      } catch {
        // se fallisce: per safety chiediamo accettazione
        if (mounted) setData({ required: true, accepted: false, version: "unknown", text: "" });
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [onAccepted]);

  const accept = async () => {
    if (!checked || !data?.version) return;
    setBusy(true);
    try {
      await api.post("/medico/responsabilita/accept", { version: data.version, accepted: true });
      toast.success("Accesso responsabile registrato");
      onAccepted && onAccepted();
    } catch (e) {
      toast.error(apiErr(e, "Errore registrazione accettazione"));
    } finally {
      setBusy(false);
    }
  };

  const downloadPdf = async () => {
    try {
      const r = await api.get("/medico/responsabilita/pdf", { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "primevision-informativa-medico.pdf";
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(apiErr(e, "Errore download PDF"));
    }
  };

  if (loading) return null;
  if (!data?.required) return null;

  const fullText = data.text || "Accedendo all'area clinica dichiaro di essere un professionista sanitario abilitato...";

  return (
    <div className="fixed inset-0 z-[100] bg-brand-navy/95 backdrop-blur flex items-center justify-center p-4 overflow-auto" data-testid="responsabilita-gate">
      <div className="bg-white rounded-2xl w-full max-w-2xl shadow-2xl my-8">
        <div className="px-6 py-5 border-b border-[#E2E8F0] flex items-center gap-3">
          <PrimeVisionLogo size={28} />
          <div>
            <h2 className="font-extrabold text-brand-navy text-lg leading-tight">Accesso area clinica Prime Vision</h2>
            <div className="text-xs text-slate-500">Dr/Dr.ssa {user?.name || ""} · versione {data.version}</div>
          </div>
        </div>
        <div className="px-6 py-5">
          <div className="flex items-start gap-2 mb-3">
            <Shield size={16} className="text-brand-azure mt-0.5 shrink-0" />
            <p className="text-sm text-slate-700 leading-relaxed">
              Prima di accedere all'area clinica, leggi e conferma le tue responsabilità professionali e privacy.
            </p>
          </div>
          <div className={`bg-slate-50 border border-[#E2E8F0] rounded-lg p-4 text-xs text-slate-700 leading-relaxed whitespace-pre-line ${expanded ? "max-h-[420px] overflow-auto" : "max-h-32 overflow-hidden relative"}`}
               data-testid="responsabilita-text">
            {fullText}
            {!expanded && (
              <div className="absolute bottom-0 left-0 right-0 h-12 bg-gradient-to-t from-slate-50 to-transparent pointer-events-none" />
            )}
          </div>
          <button
            data-testid="responsabilita-expand"
            onClick={() => setExpanded((x) => !x)}
            className="mt-2 text-xs font-bold text-brand-azure hover:underline"
          >
            {expanded ? "Riduci testo" : "Leggi responsabilità privacy e uso deontologico (intero testo)"}
          </button>

          <button
            data-testid="responsabilita-download-pdf"
            onClick={downloadPdf}
            className="mt-4 inline-flex items-center gap-1.5 text-xs font-bold text-brand-navy hover:bg-slate-100 px-2 py-1 rounded-lg"
          >
            <FileDown size={13} /> Scarica testo informativa medico (PDF)
          </button>

          <label className="mt-5 flex items-start gap-2 cursor-pointer" data-testid="responsabilita-checkbox-label">
            <input
              type="checkbox"
              checked={checked}
              onChange={(e) => setChecked(e.target.checked)}
              className="mt-0.5 rounded border-slate-400"
              data-testid="responsabilita-checkbox"
            />
            <span className="text-sm text-slate-800 leading-snug">
              Ho letto e accetto le responsabilità professionali, privacy e deontologiche descritte sopra. Confermo di essere un professionista sanitario abilitato.
            </span>
          </label>
        </div>
        <div className="px-6 py-4 bg-slate-50 border-t border-[#E2E8F0] flex justify-end">
          <button
            data-testid="responsabilita-accept-btn"
            disabled={!checked || busy}
            onClick={accept}
            className="bg-brand-navy hover:bg-brand-navyDark text-white font-bold px-5 py-2.5 rounded-lg flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Check size={15} /> Accedi all'area clinica
          </button>
        </div>
      </div>
    </div>
  );
}
